// W-B.R15 · patch focus mode (Vania 10 Oct 23:12).
//
// R14 reached scrollTop at every viewport but the poster rendered 100x100
// at 1280x620 because the sticky header ate the column. R15 reshapes patch
// mode as a full editor that takes over the whole right side of the admin
// (work area + details column). Details form state is preserved via the
// parent · we only hide it visually.
//
// Patch mode layout:
//   >= 1024 wide: horizontal · poster-area (flex-1) | rail (280px fixed)
//   <  1024 wide: vertical  · poster-area (flex-1) above a 96px compact
//                             two-row strip of controls at the bottom.
// Idle: a thin inline action row renders under the preview as before.
// before-after: unchanged split view.
//
// Earlier R12-R14 behaviour kept:
//   - smart / clone / flat fill modes · body sent to /patch-cover.
//   - clone needs a target + source rect pair.
//   - zoom fit (poster fills the live container, no scroll) · 100 / 150 /
//     200 zoom scroll inside patch-canvas-scroll.
//   - normalised 0-1 patch rects.
//   - keyboard scroll (PageUp/Down, ArrowUp/Down, Home/End) scoped away
//     from form inputs.
//   - jump-to-top / jump-to-bottom buttons.
//   - upload cover + open original screenshot.
//   - persistent applied banner after apply.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";

const KILI = "#FFD60A";
const INK = "#1A1A30";

function clamp01(n) { return Math.max(0, Math.min(1, n)); }

export default React.forwardRef(function PatchToolPanel({
  pendingId,
  driveFileId,
  coverUrl,
  originalCoverUrl,
  onApplied,
  onRestored,
  onModeChange,
  embedded = false,  // W-B.R16 · when true: always-patch mode, no idle,
                      // no rail done + apply · top bar owns both.
}, ref) {
  const [mode, setMode] = useState(embedded ? "patch" : "idle");             // "idle" | "patch" | "before-after"
  const [fillMode, setFillMode] = useState("smart");    // "smart" | "clone" | "flat"
  const [patches, setPatches] = useState([]);
  const [draft, setDraft] = useState(null);
  const [cloneStage, setCloneStage] = useState("target"); // "target" | "source"
  const [pendingClone, setPendingClone] = useState(null); // {target: rect}
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);          // {rects, at, ms, modeUsed}
  const [imgDims, setImgDims] = useState(null);
  const [zoom, setZoom] = useState("fit");               // "fit" | 1 | 1.5 | 2
  const [uploading, setUploading] = useState(false);
  const [fitSize, setFitSize] = useState({ w: 300, h: 400 });
  const [fitMode, setFitMode] = useState("width");  // R18 · "width" | "all" (fit entire poster)
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const rawUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/drive-watcher/blob/${driveFileId}`;
  const patchSrc = coverUrl || rawUrl;
  const patchActive = mode === "patch";

  // R14/R15 · parent tracks patchActive so review-work-area can expand,
  // the preview card can collapse, and review-details can hide (its form
  // state is preserved because the <div> stays mounted, just hidden).
  useEffect(() => { onModeChange?.(mode); }, [mode, onModeChange]);

  const onPointerDown = (e) => {
    if (!patchActive || !canvasRef.current) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);
    dragRef.current = { x, y };
    setDraft({ left: x, top: y, right: x, bottom: y });
  };
  const onPointerMove = useCallback((e) => {
    if (!dragRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);
    const s = dragRef.current;
    setDraft({
      left: Math.min(s.x, x), top: Math.min(s.y, y),
      right: Math.max(s.x, x), bottom: Math.max(s.y, y),
    });
  }, []);
  const onPointerUp = useCallback(() => {
    if (!dragRef.current || !draft) { dragRef.current = null; return; }
    const good = (draft.right - draft.left) >= 0.01 && (draft.bottom - draft.top) >= 0.01;
    if (good) {
      if (fillMode === "clone" && cloneStage === "target") {
        setPendingClone({ target: draft });
        setCloneStage("source");
        toast.info("now drag a clean source box");
      } else if (fillMode === "clone" && cloneStage === "source" && pendingClone) {
        setPatches((ps) => [...ps, { ...pendingClone.target, source: draft }]);
        setPendingClone(null);
        setCloneStage("target");
      } else {
        setPatches((ps) => [...ps, draft]);
      }
    }
    setDraft(null);
    dragRef.current = null;
  }, [draft, fillMode, cloneStage, pendingClone]);
  useEffect(() => {
    if (!patchActive) return;
    const up = () => onPointerUp();
    const move = (e) => onPointerMove(e);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [patchActive, onPointerMove, onPointerUp]);

  // R14 · recompute fit size when the scroll container resizes.
  useEffect(() => {
    if (!patchActive) return;
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => {
      if (!scrollRef.current) return;
      const cw = Math.max(80, scrollRef.current.clientWidth - 8);
      const ch = Math.max(80, scrollRef.current.clientHeight - 8);
      const ar = (imgDims && imgDims.naturalWidth && imgDims.naturalHeight)
        ? imgDims.naturalWidth / imgDims.naturalHeight
        : 3 / 4;
      // R18 CHECK 3 · "fit all" lets Vania see the whole poster once
      // before patching; the default "fit width" still fills horizontal
      // space so ultra-portrait posters stay large enough for drawing.
      if (fitMode === "all") {
        let w = cw;
        let h = w / ar;
        if (h > ch) { h = ch; w = ch * ar; }
        setFitSize({ w: Math.max(100, w), h: Math.max(100, h) });
      } else {
        const w = cw;
        const h = w / ar;
        setFitSize({ w: Math.max(100, w), h: Math.max(100, h) });
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [patchActive, imgDims, fitMode]);

  // R14 · keyboard scroll while patch mode is on, scoped away from inputs.
  useEffect(() => {
    if (!patchActive) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      const el = scrollRef.current;
      if (!el) return;
      const step = 60;
      if (e.key === "PageDown") { el.scrollBy({ top: el.clientHeight * 0.9, behavior: "smooth" }); e.preventDefault(); }
      else if (e.key === "PageUp") { el.scrollBy({ top: -el.clientHeight * 0.9, behavior: "smooth" }); e.preventDefault(); }
      else if (e.key === "ArrowDown") { el.scrollBy({ top: step }); e.preventDefault(); }
      else if (e.key === "ArrowUp") { el.scrollBy({ top: -step }); e.preventDefault(); }
      else if (e.key === "End") { el.scrollTo({ top: el.scrollHeight }); e.preventDefault(); }
      else if (e.key === "Home") { el.scrollTo({ top: 0 }); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [patchActive]);

  const togglePatchMode = () => setMode((m) => (m === "patch" ? "idle" : "patch"));
  const toggleBeforeAfter = () => setMode((m) => (m === "before-after" ? "idle" : "before-after"));
  const closePatch = () => setMode("idle");
  const jumpTop = () => scrollRef.current && scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  const jumpBottom = () => scrollRef.current && scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });

  const apply = async () => {
    if (patches.length === 0) { toast.error("drag a box over the icon first"); return; }
    setApplying(true);
    const started = performance.now();
    try {
      const r = await client.post(
        `/admin/drive-watcher/patch-cover/${encodeURIComponent(pendingId)}`,
        { patches, mode: fillMode },
        { timeout: 25000, validateStatus: () => true },
      );
      const ms = Math.round(performance.now() - started);
      const data = r && r.data;
      if (!data || typeof data !== "object") {
        toast.error(`patch failed · bad response (${r?.status || "?"}) in ${ms}ms`); return;
      }
      if (data.ok === false || (r.status >= 400 && !data.ok)) {
        toast.error(`patch failed · ${data.error || data.detail || `HTTP ${r.status}`}`); return;
      }
      const n = data.rects || patches.length;
      const at = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      setPatches([]); setDraft(null); setPendingClone(null); setCloneStage("target");
      setApplied({ rects: n, at, ms, modeUsed: data.mode || fillMode });
      toast.success(`patched · ${n} box${n === 1 ? "" : "es"} · ${data.mode || fillMode} · ${ms}ms`);
      onApplied?.(data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || err?.message || "patch failed";
      toast.error(`patch failed · ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
    } finally {
      setApplying(false);
    }
  };

  // W-B.R25.2 · undo semantics:
  //   1. If any patch rects exist (drawn or preset), pop the LAST one
  //      so Vania can walk back the icons-preset drop without clearing
  //      the whole canvas.
  //   2. Else, if a server-side applied patch exists, restore the
  //      original cover.
  //   3. Else, the button should be disabled (surfaced via
  //      getUndoLockedReason() so PosterEditor can wire aria-disabled).
  const undo = async () => {
    if (patches.length > 0) {
      setPatches((ps) => ps.slice(0, -1));
      setDraft(null); setPendingClone(null); setCloneStage("target");
      toast.success("removed last box");
      return;
    }
    if (!applied && !originalCoverUrl) {
      toast.error("nothing to undo · no boxes drawn and no original stored");
      return;
    }
    try {
      const r = await client.post(`/admin/drive-watcher/restore-original/${encodeURIComponent(pendingId)}`, {}, { timeout: 15000, validateStatus: () => true });
      const data = r && r.data;
      if (data && data.ok) {
        toast.success("restored original"); setApplied(null); onRestored?.(data); return;
      }
      const reason = (data && (data.error || data.detail)) || `HTTP ${r?.status || "?"}`;
      if (originalCoverUrl) {
        onRestored?.({ cover_image_url: originalCoverUrl }); setApplied(null);
        toast.success("restored original (client-side)");
      } else { toast.error(`undo failed · ${reason}`); }
    } catch (err) {
      if (originalCoverUrl) { onRestored?.({ cover_image_url: originalCoverUrl }); setApplied(null); }
      else { toast.error(`undo failed · ${err?.message || "no original stored"}`); }
    }
  };

  const removePatch = (idx) => setPatches((ps) => ps.filter((_, i) => i !== idx));
  const clearDrafts = () => { setPatches([]); setDraft(null); setPendingClone(null); setCloneStage("target"); };

  // W-B.R16 · PosterEditor top bar primary action pulls apply() and the
  // live box count through this ref.
  // W-B.R22 · also expose fillMode + the count of patches still missing
  // a source rect so the Apply button can hard-lock in clone mode.
  React.useImperativeHandle(ref, () => ({
    apply,
    undo,
    getBoxCount: () => patches.length,
    getFillMode: () => fillMode,
    getMissingSourceCount: () => fillMode === "clone" ? patches.filter((p) => !p.source).length : 0,
  }), [apply, undo, patches.length, fillMode, patches]);

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post(`/admin/drive-watcher/upload-cover/${encodeURIComponent(pendingId)}`, fd, { timeout: 60000, validateStatus: () => true, headers: { "Content-Type": "multipart/form-data" } });
      const data = r && r.data;
      if (data && data.ok) {
        toast.success("uploaded cover");
        setApplied({ rects: 0, at: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }), ms: 0, modeUsed: "upload" });
        onApplied?.(data);
      } else {
        toast.error(`upload failed · ${data?.error || data?.detail || "unknown"}`);
      }
    } catch (e) {
      toast.error(`upload failed · ${e?.message || e}`);
    } finally {
      setUploading(false); ev.target.value = "";
    }
  };

  const pct = (n) => `${(n * 100).toFixed(2)}%`;
  const canvasAspect = (imgDims && imgDims.naturalWidth && imgDims.naturalHeight)
    ? `${imgDims.naturalWidth} / ${imgDims.naturalHeight}`
    : "3 / 4";
  const canvasSizeStyle = zoom === "fit"
    ? { width: `${fitSize.w}px`, height: `${fitSize.h}px`, alignSelf: "center" }
    : { width: `${(imgDims?.naturalWidth || 480) * zoom}px`,
        height: `${(imgDims?.naturalHeight || 640) * zoom}px`,
        alignSelf: "flex-start" };

  // ─────────────────────────────────────────────────────────────
  // IDLE · just the inline enter-patch button row (sits under the
  // preview card so the admin can click into the full editor).
  // W-B.R16 · never rendered when embedded (editor always-patch).
  // ─────────────────────────────────────────────────────────────
  if (mode === "idle" && !embedded) {
    return (
      <div data-testid="patch-tool-panel" className="mx-auto mt-3 flex flex-col gap-2" style={{ maxWidth: 512 }}>
        <div className="flex flex-wrap items-stretch gap-2">
          <button type="button" data-testid="patch-tool-button" onClick={togglePatchMode}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 px-4 py-3 text-sm font-bold uppercase tracking-wider"
                  style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
            <span className="whitespace-nowrap">patch</span>
            <span aria-hidden className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-sm px-1 text-[10px] font-bold" style={{ background: INK, color: KILI, lineHeight: 1 }}>p</span>
          </button>
          <button type="button" data-testid="patch-before-after" onClick={toggleBeforeAfter}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border px-4 py-3 text-xs font-semibold"
                  style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
            <span className="whitespace-nowrap">before / after</span>
          </button>
          <button type="button" data-testid="patch-undo" onClick={undo}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border px-3 py-3 text-xs font-semibold hover:bg-neutral-50"
                  style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>
            <span className="whitespace-nowrap">undo (restore original)</span>
          </button>
        </div>
        {applied && (
          <div data-testid="patch-applied-banner"
               className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-[12px]"
               style={{ background: "#F1E7D6", borderColor: INK, color: INK }}>
            <span className="whitespace-nowrap">
              patched · {applied.rects} box{applied.rects === 1 ? "" : "es"} · {applied.modeUsed} · {applied.at} · undo available
            </span>
            <button type="button" data-testid="patch-applied-undo" onClick={undo}
                    className="whitespace-nowrap rounded border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
                    style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>undo</button>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // BEFORE / AFTER · unchanged split view (lives inside work-area).
  // ─────────────────────────────────────────────────────────────
  if (mode === "before-after") {
    return (
      <div data-testid="patch-tool-panel" className="mx-auto mt-3 flex flex-col gap-2" style={{ maxWidth: 720 }}>
        <div className="flex gap-2">
          <button type="button" onClick={closePatch} data-testid="patch-done"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border px-3 py-2 text-xs font-semibold"
                  style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
            <span className="whitespace-nowrap">done</span>
          </button>
        </div>
        <div data-testid="patch-before-after-view" className="grid grid-cols-2 gap-2 rounded-md border bg-white p-2 shadow-sm" style={{ borderColor: "#E4D7C0" }}>
          <figure>
            <figcaption className="mb-1 text-[10px] uppercase tracking-wider" style={{ color: "#8A8171" }}>before (original)</figcaption>
            <img data-testid="patch-img-before" src={originalCoverUrl || coverUrl || rawUrl} alt="before" className="block w-full rounded border" style={{ borderColor: "#E4D7C0" }} />
          </figure>
          <figure>
            <figcaption className="mb-1 text-[10px] uppercase tracking-wider" style={{ color: "#8A8171" }}>after (patched)</figcaption>
            <img data-testid="patch-img-after" src={coverUrl || rawUrl} alt="after" className="block w-full rounded border" style={{ borderColor: "#E4D7C0" }} />
          </figure>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // PATCH FOCUS · poster-area + rail. Rail is a right-side column
  // at lg+ (260-300px wide) and a bottom 96px strip under 1024.
  // ─────────────────────────────────────────────────────────────
  const Rail = (
    <aside data-testid="patch-rail"
           className="flex shrink-0 min-h-0 min-w-0 border-l bg-white
                      md:order-last md:h-full md:w-[260px] md:flex-col md:overflow-y-auto md:p-2 md:gap-2
                      order-last h-[96px] w-full flex-row items-stretch gap-2 overflow-x-auto overflow-y-hidden border-l-0 border-t p-2"
           style={{ borderColor: "#E4D7C0" }}>
      {/* done · hidden when embedded · top bar owns Back */}
      {!embedded && (
      <button type="button" data-testid="patch-done" onClick={closePatch}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border-2 px-3 py-2 text-xs font-bold uppercase tracking-wider md:w-full"
              style={{ borderColor: INK, background: KILI, color: INK }}>
        <span className="whitespace-nowrap">done · back to form</span>
      </button>
      )}

      {/* fill mode · smart / clone / flat */}
      <div className="flex shrink-0 flex-row items-center gap-1 md:flex-row md:flex-wrap">
        <span className="text-[10px] uppercase tracking-wider md:w-full" style={{ color: "#8A8171" }}>fill mode</span>
        {["smart", "clone", "flat"].map((m) => (
          <button key={m} type="button"
                  data-testid={`patch-mode-${m}`}
                  onClick={() => { setFillMode(m); setPendingClone(null); setCloneStage("target"); }}
                  title={m === "smart" ? "best for busy backgrounds · uses OpenCV inpaint"
                         : m === "clone" ? "copy a clean patch from elsewhere · requires a source box"
                         : "replace with the nearest edge colour · best for plain backgrounds only"}
                  className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                  style={fillMode === m
                    ? { borderColor: INK, background: INK, color: "#FFFFFF" }
                    : { borderColor: "#8A8171", background: "#FFFFFF", color: "#5C5548" }}>
            {m === "smart" ? "smart" : m === "clone" ? "clone" : "flat (plain backgrounds only)"}
          </button>
        ))}
      </div>

      {/* W-B.R13 · 1-tap "icons preset" · drops 4 standard corner boxes
          where social-media overlay icons usually sit. Patches are
          normalised 0-1, so they scale across every poster aspect. */}
      <button type="button" data-testid="patch-preset-icons"
              onClick={() => {
                const ICON = 0.14;
                const PAD = 0.015;
                const corners = [
                  { left: PAD,             top: PAD,             right: PAD + ICON,       bottom: PAD + ICON },        // top-left
                  { left: 1 - PAD - ICON,  top: PAD,             right: 1 - PAD,          bottom: PAD + ICON },        // top-right
                  { left: PAD,             top: 1 - PAD - ICON,  right: PAD + ICON,       bottom: 1 - PAD },           // bottom-left
                  { left: 1 - PAD - ICON,  top: 1 - PAD - ICON,  right: 1 - PAD,          bottom: 1 - PAD },           // bottom-right
                ];
                setPatches((ps) => [...ps, ...corners]);
                setDraft(null);
                toast.success("added 4 corner icon boxes · adjust each and apply");
              }}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold md:w-full md:px-3"
              style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
        <span className="whitespace-nowrap">icons preset · 4 corners</span>
      </button>

      {/* apply · hidden when embedded · top bar owns primary */}
      {!embedded && (
      <button type="button" data-testid="patch-apply" onClick={apply} disabled={applying || patches.length === 0}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border-2 px-3 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50 md:w-full"
              style={{ borderColor: INK, background: KILI, color: INK }}>
        <span className="whitespace-nowrap">{applying ? "applying…" : `apply${patches.length > 0 ? ` · ${patches.length}` : ""}`}</span>
      </button>
      )}

      {/* undo / clear */}
      <button type="button" data-testid="patch-undo" onClick={undo}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold md:w-full md:px-3"
              style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>
        <span className="whitespace-nowrap">undo</span>
      </button>
      {patches.length > 0 && (
        <button type="button" data-testid="patch-clear" onClick={clearDrafts}
                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold md:w-full md:px-3"
                style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>
          <span className="whitespace-nowrap">clear boxes</span>
        </button>
      )}

      {/* boxes list · lg only · delete per box */}
      <div data-testid="patch-boxes-list" className="hidden shrink-0 flex-col gap-1 rounded-md border bg-neutral-50 p-2 text-[11px] md:flex" style={{ borderColor: "#E4D7C0", color: "#5C5548" }}>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#8A8171" }}>boxes · {patches.length}</span>
        {patches.length === 0 && <span className="text-neutral-500">drag over an icon on the poster</span>}
        {patches.map((p, i) => (
          <div key={i} data-testid={`patch-box-row-${i}`} className="flex items-center justify-between gap-1">
            <span>#{i + 1} · {(p.right - p.left).toFixed(2)} × {(p.bottom - p.top).toFixed(2)}</span>
            <button type="button" data-testid={`patch-box-remove-${i}`} onClick={() => removePatch(i)}
                    className="whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase"
                    style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>remove</button>
          </div>
        ))}
      </div>

      {/* zoom + jump · one row in lg too */}
      <div className="flex shrink-0 flex-row flex-wrap items-center gap-1">
        <span className="text-[10px] uppercase tracking-wider md:w-full" style={{ color: "#8A8171" }}>zoom</span>
        {[["fit", "fit"], [1, "100"], [1.5, "150"], [2, "200"]].map(([v, label]) => (
          <button key={label} type="button"
                  data-testid={`patch-zoom-${label}`}
                  onClick={() => setZoom(v)}
                  className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                  style={zoom === v
                    ? { borderColor: INK, background: INK, color: "#FFFFFF" }
                    : { borderColor: "#8A8171", background: "#FFFFFF", color: "#5C5548" }}>
            {label === "fit" ? "fit" : `${label}%`}
          </button>
        ))}
        {/* R18 CHECK 3 · fit-all · shows the whole poster regardless of aspect */}
        <button type="button" data-testid="patch-zoom-fit-all"
                onClick={() => { setZoom("fit"); setFitMode(fitMode === "all" ? "width" : "all"); }}
                className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                style={fitMode === "all"
                  ? { borderColor: INK, background: INK, color: "#FFFFFF" }
                  : { borderColor: "#8A8171", background: "#FFFFFF", color: "#5C5548" }}>
          fit all
        </button>
        <button type="button" data-testid="patch-scroll-top" onClick={jumpTop}
                className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>top</button>
        <button type="button" data-testid="patch-scroll-bottom" onClick={jumpBottom}
                className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>bottom</button>
      </div>

      {/* before/after + upload + open original */}
      <button type="button" data-testid="patch-before-after" onClick={toggleBeforeAfter}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold md:w-full md:px-3"
              style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
        <span className="whitespace-nowrap">before / after</span>
      </button>
      <label data-testid="patch-upload-cover"
             className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold hover:bg-neutral-50 md:w-full md:px-3"
             style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>
        <span className="whitespace-nowrap">{uploading ? "uploading…" : "upload cover"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
      </label>
      <a data-testid="patch-open-original" href={rawUrl} target="_blank" rel="noreferrer"
         className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-[11px] font-semibold hover:bg-neutral-50 md:w-full md:px-3"
         style={{ borderColor: "#8A8171", color: "#5C5548", background: "#FFFFFF" }}>
        <span className="whitespace-nowrap">open original</span>
      </a>

      {/* persistent applied banner */}
      {applied && (
        <div data-testid="patch-applied-banner"
             className="flex shrink-0 flex-col gap-1 rounded-md border px-2 py-2 text-[11px] md:w-full"
             style={{ background: "#F1E7D6", borderColor: INK, color: INK }}>
          <span className="whitespace-nowrap">patched · {applied.rects} box{applied.rects === 1 ? "" : "es"} · {applied.modeUsed} · {applied.at}</span>
          <button type="button" data-testid="patch-applied-undo" onClick={undo}
                  className="w-full whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>undo</button>
        </div>
      )}
    </aside>
  );

  return (
    <div data-testid="patch-tool-panel" data-patch-focus="1"
         className="flex h-full min-h-0 w-full flex-col md:flex-row">
      {/* Poster area · left · flex-1 fills remaining width (and height on <lg). */}
      <div data-testid="patch-poster-area"
           className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-white p-1"
           style={{ borderColor: "#E4D7C0" }}>
        <div className="mb-1 flex shrink-0 flex-wrap items-center justify-between gap-2 px-1 text-[11px]" style={{ color: "#5C5548" }}>
          <span data-testid="patch-hint">
            {fillMode === "clone"
              ? (cloneStage === "target"
                  ? "clone · drag the TARGET box over the icon · Apply stays locked until a source box is set"
                  : "clone · now drag a SOURCE box over a clean area · shown as a dashed blue line")
              : "drag a box over each app icon · then tap apply on the right"}
          </span>
          <span className="text-[10px] uppercase tracking-wider" style={{ color: "#8A8171" }}>
            {zoom === "fit" ? "fit" : `zoom ${(zoom * 100) | 0}%`} · {patches.length} box{patches.length === 1 ? "" : "es"}
          </span>
        </div>
        {/* W-B.R13 · smart-fill overlap hint. Smart inpaint is strong on
            background texture but can smudge text/faces. Nudge Vania to
            use clone when a box is likely to sit on top of either. */}
        {fillMode === "smart" && (patches.length > 0 || draft) && (
          <div data-testid="patch-smart-overlap-hint"
               className="mb-1 shrink-0 rounded-md border px-2 py-1 text-[11px]"
               style={{ background: "#FDF3DC", borderColor: "#B2550D", color: "#7A3C09" }}>
            ⚠ smart fill works best on backgrounds · if your box overlaps text or faces, try clone or flat instead
          </div>
        )}
        {/* W-B.R13 · clone-mode guard rail. Keep Vania oriented between
            the two drag stages and remind her the Apply button stays
            locked until the source is set. */}
        {fillMode === "clone" && cloneStage === "source" && pendingClone?.target && (
          <div data-testid="patch-clone-source-hint"
               className="mb-1 shrink-0 rounded-md border px-2 py-1 text-[11px]"
               style={{ background: "#DBEAFE", borderColor: "#1E40AF", color: "#1E3A8A" }}>
            drag a source box over a clean area · it will show as a dashed blue line · Apply unlocks once the source is set
          </div>
        )}
        <div ref={scrollRef}
             data-testid="patch-canvas-scroll"
             className="flex min-h-0 flex-1 justify-center overflow-auto rounded-md border bg-neutral-50"
             style={{ borderColor: "#E4D7C0", touchAction: "pan-x pan-y", overscrollBehavior: "contain" }}>
          <div ref={canvasRef}
               data-testid="patch-canvas"
               className="relative mx-auto my-auto select-none overflow-hidden"
               style={{ ...canvasSizeStyle, flexShrink: 0, touchAction: "none" }}
               onPointerDown={onPointerDown}>
            <img src={patchSrc}
                 alt="current cover"
                 draggable={false}
                 loading="eager"
                 decoding="async"
                 onLoad={(e) => setImgDims({ naturalWidth: e.currentTarget.naturalWidth, naturalHeight: e.currentTarget.naturalHeight })}
                 className="pointer-events-none block select-none"
                 style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
            {patches.map((p, i) => (
              <React.Fragment key={i}>
                <div data-testid={`patch-rect-${i}`}
                     style={{ position: "absolute", left: pct(p.left), top: pct(p.top), width: pct(p.right - p.left), height: pct(p.bottom - p.top), background: "rgba(255,214,10,0.65)", outline: `2px solid ${INK}` }} />
                {p.source && (
                  <div data-testid={`patch-rect-${i}-source`}
                       style={{ position: "absolute", left: pct(p.source.left), top: pct(p.source.top), width: pct(p.source.right - p.source.left), height: pct(p.source.bottom - p.source.top), outline: `2px dashed #1E40AF`, background: "rgba(30,64,175,0.15)" }} />
                )}
              </React.Fragment>
            ))}
            {pendingClone?.target && (
              <div data-testid="patch-rect-pending"
                   style={{ position: "absolute", left: pct(pendingClone.target.left), top: pct(pendingClone.target.top), width: pct(pendingClone.target.right - pendingClone.target.left), height: pct(pendingClone.target.bottom - pendingClone.target.top), outline: `2px dashed ${INK}`, background: "rgba(255,214,10,0.25)" }} />
            )}
            {draft && (
              <div data-testid="patch-rect-draft"
                   style={{ position: "absolute", left: pct(draft.left), top: pct(draft.top), width: pct(draft.right - draft.left), height: pct(draft.bottom - draft.top),
                            background: fillMode === "clone" && cloneStage === "source" ? "rgba(30,64,175,0.25)" : "rgba(255,214,10,0.4)",
                            outline: fillMode === "clone" && cloneStage === "source" ? "2px dashed #1E40AF" : `2px dashed ${INK}` }} />
            )}
          </div>
        </div>
      </div>
      {Rail}
    </div>
  );
});
